import { queryParamKeys } from 'modules/experiments/constants';
import { QueryParam, TableFacetContextState, TableFilter } from 'modules/experiments/types';
import { FC, useEffect, useState } from 'react';
import {
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { encodeMenuId, processTableFilterForSelectedFilters } from '../utils';
import { TableFacetContext, initialTableFacetContextState } from './TableFacetContext';
import { useTableFilterQueryParamContext } from './TableFilterQueryParamContext';
import { useTablePaginationContext } from './TablePaginationContext';

export const TableFacetContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const { resetPage } = useTablePaginationContext();

  /** Free form search */
  const [search, setSearch] = useSearchParamState({
    searchParamKey: queryParamKeys.search,
    stateValueWhenSearchParamIsUnset: initialTableFacetContextState.search,
    ...serializationOptionsForStringValue,
  });

  /** Experiments state tabs */
  const [selectedTab, setSelectedTab] = useSearchParamState({
    searchParamKey: queryParamKeys.selectedTab,
    stateValueWhenSearchParamIsUnset: initialTableFacetContextState.selectedTab,
    ...serializationOptionsForStringArrayValue,
  });

  /** Experiment and Campaign Filters */
  const {
    campaignLabels,
    campaignMedium,
    campaignTypes,
    experimentModifiedBy,
    experimentTestSubject,
    experimentSystemConversionWinningCriteria,
    experimentCustomConversionWinningCriteria,
    experimentWinnerSelected,
    experimentSplitVariationsRandomly,
    queryParamMap,
    clearQueryParam,
  } = useTableFilterQueryParamContext();

  const updateSelectedFiltersOnUpdate = [
    campaignLabels,
    campaignMedium,
    campaignTypes,
    experimentModifiedBy,
    experimentTestSubject,
    experimentSystemConversionWinningCriteria,
    experimentCustomConversionWinningCriteria,
    experimentWinnerSelected,
    experimentSplitVariationsRandomly,
  ];

  /**
   * Update the selected filter array as the url search params updates
   */
  const [selectedFilters, setSelectedFilters] = useState(
    initialTableFacetContextState.selectedFilters,
  );

  useEffect(() => {
    const updatedSelectedFilters = (
      Object.entries(queryParamMap) as [
        TableFilter,
        QueryParam<string[]> | QueryParam<string> | QueryParam<number[]>,
      ][]
    ).reduce<string[]>((acc, [tableFilter, searchParam]) => {
      if (searchParam.value.length) {
        if (Array.isArray(searchParam.value)) {
          return [
            ...acc,
            ...searchParam.value.map((val) =>
              encodeMenuId(processTableFilterForSelectedFilters(tableFilter), val.toString()),
            ),
          ];
        } else {
          return [
            ...acc,
            encodeMenuId(processTableFilterForSelectedFilters(tableFilter), searchParam.value),
          ];
        }
      }
      return acc;
    }, []);

    setSelectedFilters(updatedSelectedFilters);
  }, updateSelectedFiltersOnUpdate);

  const clearSelectedFilters = () => {
    clearQueryParam();
    setSelectedTab(initialTableFacetContextState.selectedTab);
  };

  const context: TableFacetContextState = {
    search,
    setSearch: (searchTerm: string) => {
      resetPage();
      setSearch(searchTerm);
    },
    selectedTab,
    setSelectedTab: (experimentState: string[]) => {
      resetPage();
      setSelectedTab(experimentState);
    },
    selectedFilters,
    clearSelectedFilters,
  };

  return <TableFacetContext.Provider value={context}>{children}</TableFacetContext.Provider>;
};
